export interface OpenGraphImage {
  type: string;
  width: number;
  height: number;
  url: string;
}

interface PageConfig {
  title: string;
  description?: string;
  noindex?: boolean;
  nofollow?: boolean;
  openGraph?: {
    images: OpenGraphImage[];
  };
}

// Shared with MaticPageMeta, which falls back to these when a page names no
// image of its own — which every page currently does.
export const DEFAULT_OG_IMAGE: OpenGraphImage = {
  type: "image/jpeg",
  width: 1200,
  height: 630,
  url: "https://maticrobots.com/images/webcard-1200x630.jpg",
};

export const COMPANY_OG_IMAGE: OpenGraphImage = {
  type: "image/jpeg",
  width: 1200,
  height: 630,
  url: "https://maticrobots.com/images/webcard-company-1200x630.jpg",
};

/*
 * Deliberately NOT in the default images array, and this is load-bearing.
 * Apple's LinkPresentation tiles a preview out of every og:image a page
 * declares, so shipping this alongside the wide card made an iMessage share
 * render as two crops of the same photo side by side. One og:image, one
 * preview. Twitter reads og:image when twitter:image is absent, and
 * summary_large_image wants the wide card anyway, so nothing needs the square.
 */
export const DEFAULT_TWITTER_IMAGE: OpenGraphImage = {
  type: "image/png",
  width: 627,
  height: 627,
  url: "https://maticrobots.com/images/webcard-627x627.png",
};

/** The share card every page falls back to, per the Social share links doc. */
export const DEFAULT_TITLE =
  "Matic: The Floor-Cleaning Robot Rated 10/10 by WIRED";
export const DEFAULT_DESCRIPTION =
  "Matic sees and avoids toys, cords, and clutter while quietly vacuuming and mopping. Try risk-free for six months.";

const config: Record<string, PageConfig> = {
  product: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  checkout: {
    title: "Checkout | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  orderConfirm: {
    title: "Thank you | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  terms: {
    title: "Terms of Service | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  privacy: {
    title: "Privacy Policy | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  blog: {
    title:
      "Matic comes out of stealth with $30M in funding to launch fully autonomous indoor robots",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  betatesting: {
    title: "Beta Tester Agreement | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  warranty: {
    title: "Warranty | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  "return-policy": {
    title: "Return Policy | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  "privacy-choices": {
    title: "Do Not Sell or Share My Personal Information | Matic",
    description:
      "How to opt out of the sale or sharing of your personal information for targeted advertising.",
  },
  accessibility: {
    title: "Digital Accessibility Contact Form | Matic",
    description:
      "Tell us how we can help with your digital accessibility question or concern.",
  },
  "contact-us": {
    title: "Contact Us | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  "order-status": {
    title: "Order Status | Matic",
    description:
      "Enter your order number and billing ZIP to see where your Matic is, from the production line to your door.",
    noindex: true,
  },
  support: {
    title: "Support | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  press: {
    title: "Press | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  careers: {
    title: "Careers | Matic",
    description:
      "The world's most advanced floor cleaner. Your home's best helper.",
  },
  404: {
    title: "Page not found | Matic",
  },
  429: {
    title: "Error 429 (Too Many Requests)",
  },
  500: {
    title: "Error 500 (Server Error)",
  },
};

export default config;
